import { useState, useEffect } from "react";
import {
  Button,
  Divider,
  Grid,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Box,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { theme } from "../../../theme";
import GoogleIconPng from "../../../assests/google.png";
import ReCAPTCHA from "react-google-recaptcha";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  deleteUser,
} from "firebase/auth";
import {
  auth,
  googleProvider,
} from "../../../utils/FireBaseConfigurations/config";
import { fetchWithTimeout } from "../../../utils/fetchWithTimeout";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import confetti from "canvas-confetti";

const defaultTestKey = "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"; // Google public test key
const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

const AuthForm = ({
  initialMode = "signup",
  onSubmit,
  onSuccess,
  onForgotPassword,
  initialEmail = "",
  contactSignupData = null,	
}) => {
  const [mode, setMode] = useState(initialMode);
  const contactEmail = contactSignupData?.businessEmail || initialEmail || "";
  const [inputValues, setInputValues] = useState({
    email: contactEmail,
    password: "",
    confirmPassword: "",
  });
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [emailVerificationMessage, setEmailVerificationMessage] = useState("");
  const [verificationCheckInterval, setVerificationCheckInterval] = useState(null);
  const [hasRequestedVerification, setHasRequestedVerification] = useState(false);	
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [socialError, setSocialError] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || defaultTestKey;

const isFromContactForm = Boolean(contactSignupData?.businessEmail);
 
  useEffect(() => {
    if (isFromContactForm && contactSignupData?.businessEmail) {
      setInputValues((prev) => ({
        ...prev,
        email: contactSignupData.businessEmail,
      }));
      setIsEmailVerified(false);
      setEmailVerificationMessage("");
      setHasRequestedVerification(false);
      if (verificationCheckInterval) {
        clearInterval(verificationCheckInterval);
        setVerificationCheckInterval(null);
      }
    } else if (!isFromContactForm && initialEmail) {
      setInputValues((prev) => ({ ...prev, email: initialEmail }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactSignupData, initialEmail]);
 
 
  // Email validation function - relaxed
  const isValidEmail = () => true;


  // Password validation
  const isValidPassword = (password) => {
    return (
      password.length >= 8 &&
      /[A-Z]/.test(password) &&
      /[0-9]/.test(password) &&
      /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
    );
  };

  const isSignup = mode === "signup";
  const confirmMismatch =
    isSignup &&
    inputValues.password &&
    inputValues.confirmPassword &&
    inputValues.password !== inputValues.confirmPassword;

  const isSubmitDisabled =
    !recaptchaToken ||
    !inputValues.email ||
    !inputValues.password ||
    (isSignup && (!inputValues.confirmPassword || confirmMismatch));
 // Handle email field blur - verify email exists when user moves to password field (sign-in mode only)
const handleEmailBlur = async () => {
 if (isSignup) return;
    setEmailTouched(true);
 
    const normalizedEmail = (inputValues.email || "").trim().toLowerCase();
    if (!normalizedEmail) {
      setEmailError("Please enter your email address");
      return;
    }
 
    // For sign-in: check if user exists in database 
    setEmailError("");
    setEmailCheckLoading(true);
 
    try {
      const response = await fetchWithTimeout(
        `${BACKEND_URL}/user/check/${encodeURIComponent(normalizedEmail)}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
        30000
      );
 
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        setEmailError(
          errorData.detail || "Unable to verify email. Please try again."
        );
        return;
      }
 
      const data = await response.json();
      if (!data?.is_registered) {
        setEmailError("Email Not Found");
      } else {
        setEmailError("");
      }
    } catch (err) {
      console.error("Email verification failed:", err);
      setEmailError("Unable to verify email. Please try again.");
    } finally {
      setEmailCheckLoading(false);
    }
  };
  const fields = [
    {
      label: "Email",
      type: "email",
      name: "email",
      show: true,
      error: !isSignup && emailTouched && Boolean(emailError),
      helperText: !isSignup && emailTouched
        ? emailError || (emailCheckLoading ? "Checking email..." : "")
    :"",
    },
    { label: "Password", type: "password", name: "password", show: true },
    {
      label: "Confirm password",
      type: "password",
      name: "confirmPassword",
      show: isSignup,
      error: confirmMismatch,
      helperText: confirmMismatch
        ? "Password and Confirm Password must match"
        : "",
    },
  ];

  const socialProviders = [
    { label: "Google", src: GoogleIconPng, key: "google" },
  ];

  useEffect(() => {
    return () => {
      if (verificationCheckInterval) {
        clearInterval(verificationCheckInterval);
      }
    };
  }, [verificationCheckInterval]);
 
  const checkEmailVerification = async (email) => {
    try {
      const response = await fetchWithTimeout(
        `${BACKEND_URL}/email-verification/check/${encodeURIComponent(email)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
        10000
      );
 
      if (response.ok) {
        const data = await response.json();
        return data.is_verified;
      }
      return false;
    } catch (err) {
      console.error("Error checking email verification:", err);
      return false;
    }
  };
 
  useEffect(() => {
    if (!isFromContactForm || !inputValues.email) return;
    let isMounted = true;
 
    const verifyStatus = async () => {
      const status = await checkEmailVerification(inputValues.email);
      if (isMounted) {
        setIsEmailVerified(status);
        if (status && hasRequestedVerification) {
          setEmailVerificationMessage("Email verified successfully!");
        } else if (status && !hasRequestedVerification) {
          setEmailVerificationMessage("");
        }
      }
    };
 
    verifyStatus();
 
    return () => {
      isMounted = false;
    };
  }, [inputValues.email, isFromContactForm, hasRequestedVerification]);
 
  const sendVerificationEmail = async (email) => {
    try {
      const response = await fetchWithTimeout(
        `${BACKEND_URL}/email-verification/send`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        },
        30000
      );
 
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail || "Failed to send verification email"
        );
      }
 
      const data = await response.json();
      return data;
    } catch (err) {
      throw err;
    }
  };
 
  const ensureConfettiStyles = () => {
    if (document.getElementById("confetti-message-styles")) return;
    const style = document.createElement("style");
    style.id = "confetti-message-styles";
    style.innerHTML = `
      .confetti-message{position:fixed;top:30%;left:50%;transform:translate(-50%,-50%);background:rgba(255,255,255,.95);color:#2196f3;font-weight:bold;padding:20px 40px;border-radius:12px;font-size:1.6rem;text-align:center;z-index:9999;opacity:0;animation:confettiFadeIn .5s forwards;box-shadow:0 4px 20px rgba(0,0,0,0.3)}
      @keyframes confettiFadeIn{from{opacity:0;transform:translate(-50%,-60%)}to{opacity:1;transform:translate(-50%,-50%)}}
    `;
    document.head.appendChild(style);
  };
 
  const triggerSignupCelebration = () => {
    ensureConfettiStyles();
    try {
      confetti({
        particleCount: 500,
        spread: 70,
        origin: { y: 0.6 },
        colors: ["#2196f3", "#4caf50", "#ff9800", "#e91e63", "#9c27b0"],
      });
 
      setTimeout(() => {
        confetti({
          particleCount: 50,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: ["#2196f3", "#4caf50", "#ff9800"],
        });
        confetti({
          particleCount: 50,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: ["#2196f3", "#4caf50", "#ff9800"],
        });
      }, 500);
    } catch (err) {
      console.warn("Confetti failed:", err);
    }
 
    const msg = document.createElement("div");
    msg.className = "confetti-message";
    msg.textContent = "🎉 Congratulations! You've won 100 credits!";
    document.body.appendChild(msg);
    setTimeout(() => {
      msg.style.opacity = "0";
      msg.style.transition = "opacity 0.3s";
      setTimeout(() => msg.remove(), 300);
    }, 3000);
  };
 
  const submitLeadFormForContactUser = async (firebaseUUID) => {
    if (!contactSignupData?.businessEmail) return;
 
    const payload = {
      UUID: firebaseUUID,
      FullName: contactSignupData.fullName || contactSignupData.businessEmail,
      MobileNumber: contactSignupData.mobileNumber || "",
      BusinessEmail: contactSignupData.businessEmail,
      CompanyName: contactSignupData.companyName?.trim() || "Not Provided",
      Role: contactSignupData.role?.trim() || "Not Provided",
      NumberOfCreditsRequired: 100,
      Message: contactSignupData.message || "",
      current_loggedIn_mail: contactSignupData.businessEmail,
    };
 
    try {
      await fetchWithTimeout(
        `${BACKEND_URL}/lead/submitLeadForm`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
        30000
      );
    } catch (err) {
      console.error("Lead form submission error:", err);
    }
  };
 
  const handleEmailVerifyClick = async () => {
    if (!inputValues.email || isVerifyingEmail) return;
 
    setIsVerifyingEmail(true);
    setEmailVerificationMessage("");
    setHasRequestedVerification(true);
 
    try {
      const alreadyVerified = await checkEmailVerification(inputValues.email);
      if (alreadyVerified) {
        setIsEmailVerified(true);
        setEmailVerificationMessage("Email verified successfully!");
        setIsVerifyingEmail(false);
        return;
      }
 
      await sendVerificationEmail(inputValues.email);
      setEmailVerificationMessage(
        "Verification email sent! Please check your inbox."
      );
 
      const intervalId = setInterval(async () => {
        const verified = await checkEmailVerification(inputValues.email);
        if (verified) {
          setIsEmailVerified(true);
          setEmailVerificationMessage("Email verified successfully!");
          clearInterval(intervalId);
          setVerificationCheckInterval(null);
        }
      }, 5000);
 
      setVerificationCheckInterval(intervalId);
 
      setTimeout(() => {
        clearInterval(intervalId);
        setVerificationCheckInterval(null);
      }, 300000);
    } catch (err) {
      console.error("Email verification error:", err);
      setEmailVerificationMessage(
        err.message || "Failed to send verification email. Please try again."
      );
    } finally {
      setIsVerifyingEmail(false);
    }
  };

  // --------------------------
  // Social Login Handler
  // --------------------------
 /* const handleSocialLogin = async (providerKey) => {
    if (!onSubmit) return;

    if (providerKey === "google") {
      try {
        const result = await signInWithPopup(auth, googleProvider);
        const user = result.user;

        let accessToken = null;
        let refreshToken = null;

        if (isSignup) {
         // SIGNUP MODE: Try to register first
	  try {
            const registerResponse = await fetchWithTimeout(
              `${BACKEND_URL}/auth/registerUser`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  email: user.email.toLowerCase(),
                  unique_id: user.uid,
                  password: user.uid,
                }),
              },
              30000
            );

            if (registerResponse.ok) {
              const registerData = await registerResponse.json();
              accessToken = registerData.access_token;
              refreshToken = registerData.refresh_token || null;
            } else {
              const registerError = await registerResponse.json().catch(() => ({}));

              if (
                registerResponse.status === 400 &&
                registerError.detail === "Email already registered"
              ) {
                  setSocialError("Account already exists. Please sign in instead");
                  await signOut(auth);
                  return;
                
              } else {
                setSocialError(
                  registerError.detail ||
                    "Authentication failed. Please contact support."
                );
                await signOut(auth);
                return;
              }	    
            }
          } catch (registerErr) {
            setSocialError("Authentication successful but backend sync failed.");
            await signOut(auth);
            return;
          }
        }else {
          // SIGNIN MODE: Only try to login, don't register
          try {
            const loginResponse = await fetchWithTimeout(
              `${BACKEND_URL}/auth/loginUser`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  email: user.email.toLowerCase(),
                  password: user.uid,
                }),
              },
              30000
            );
 
            if (loginResponse.ok) {
              const loginData = await loginResponse.json();
              accessToken = loginData.access_token;
              refreshToken = loginData.refresh_token || null;
            } else {
              const loginError = await loginResponse.json().catch(() => ({}));
              console.error("Login failed:", loginError);
             
              // User doesn't exist or wrong password
              if (loginResponse.status === 401 || loginResponse.status === 404) {
                setSocialError("Account not found. Please sign up first.");
              } else {
                setSocialError(
                  loginError.detail || "Login failed. Please try again."
                );
              }
              await signOut(auth);
              return;
            }
          } catch (loginErr) {
            console.error("Backend API error:", loginErr);
            if (loginErr.name === "TimeoutError") {
              setSocialError("Request timeout - please try again");
            } else {
              setSocialError("Login failed. Please try again.");
            }
            await signOut(auth);
            return;
          }

    // Save token and complete login
        if (accessToken) {
          localStorage.setItem("access_token", accessToken);
          if (refreshToken) localStorage.setItem("refresh_token", refreshToken);
          setSocialError("");
          setError("");
          if (onSuccess) {
            const message = isSignup ? "User signed up successfully" : "User signed in successfully";
            onSuccess(message);
          }
          if (onSubmit) onSubmit();
        } else {
          setSocialError("Authentication failed. Please try again.");
          await signOut(auth);
        }
      } catch (err) {
        console.error("Google Sign-In error:", err);
        setSocialError(
          err?.message || "Google Sign-In failed. Please try again."
        );
      }
    }
  };
*/
/*
  const handleSocialLogin = async (providerKey) => {
    if (!onSubmit) return;
    if (providerKey === "google") {
      try {
        const result = await signInWithPopup(auth, googleProvider);
        const user = result.user;
 
        let accessToken = null;
        let refreshToken = null;
 
        if (isSignup) {
          // SIGNUP MODE: Try to register first
          try {
            const registerResponse = await fetchWithTimeout(
              `${BACKEND_URL}/auth/registerUser`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  email: user.email.toLowerCase(),
                  unique_id: user.uid,
                  password: user.uid,
                }),
              },
              30000
            );
 
            if (registerResponse.ok) {
              const registerData = await registerResponse.json();
              accessToken = registerData.access_token;
              refreshToken = registerData.refresh_token || null;
            } else {
              const registerError = await registerResponse
                .json()
                .catch(() => ({}));
              console.error("Registration failed:", registerError);
 
              // Check if registration failed because email already exists
              if (
                registerResponse.status === 400 &&
                registerError.detail === "Email already registered"
              ) {
                // User already exists - show error message in signup mode
                setSocialError("Account already exists. Please sign in instead.");
                await signOut(auth);
                return;
              } else {
                setSocialError(
                  registerError.detail ||
                    "Authentication failed. Please contact support."
                );
                await signOut(auth);
                return;
              }
            }
          } catch (registerErr) {
            console.error("Backend API error:", registerErr);
            if (registerErr.name === "TimeoutError") {
              setSocialError("Request timeout - please try again");
            } else {
              setSocialError(
                "Authentication successful but backend sync failed."
              );
            }
            await signOut(auth);
            return;
          }
        } else {
          // SIGNIN MODE: Only try to login, don't register
          try {
            const loginResponse = await fetchWithTimeout(
              `${BACKEND_URL}/auth/loginUser`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  email: user.email.toLowerCase(),
                  password: user.uid,
                }),
              },
              30000
            );
 
            if (loginResponse.ok) {
              const loginData = await loginResponse.json();
              accessToken = loginData.access_token;
              refreshToken = loginData.refresh_token || null;
            } else {
              const loginError = await loginResponse.json().catch(() => ({}));
              console.error("Login failed:", loginError);
             
              // User doesn't exist or wrong password
              if (loginResponse.status === 401 || loginResponse.status === 404) {
                setSocialError("Account not found. Please sign up first.");
              } else {
                setSocialError(
                  loginError.detail || "Login failed. Please try again."
                );
              }
              await signOut(auth);
              return;
            }
          } catch (loginErr) {
            console.error("Backend API error:", loginErr);
            if (loginErr.name === "TimeoutError") {
              setSocialError("Request timeout - please try again");
            } else {
              setSocialError("Login failed. Please try again.");
            }
            await signOut(auth);
            return;
          }
        }
 
        // Save token and complete login
        if (accessToken) {
          localStorage.setItem("access_token", accessToken);
          if (refreshToken) localStorage.setItem("refresh_token", refreshToken);
          setSocialError("");
          setError("");
          if (onSuccess) {
            const message = isSignup ? "User signed up successfully" : "User signed in successfully";
            onSuccess(message);
          }
          if (onSubmit) onSubmit();
        } else {
          setSocialError("Authentication failed. Please try again.");
          await signOut(auth);
        }
      } catch (err) {
        console.error("Google Sign-In error:", err);
        setSocialError(
          err?.message || "Google Sign-In failed. Please try again."
        );
      }
    }
  };
  */

const handleSocialLogin = async (providerKey) => {
  if (!onSubmit) return;
  if (providerKey === "google") {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      let accessToken = null;
      let refreshToken = null;

      // Try to login with Google UID as password
      try {
        const loginResponse = await fetchWithTimeout(
          `${BACKEND_URL}/auth/loginUser`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: user.email.toLowerCase(),
              password: user.uid,
            }),
          },
          30000
        );
if (loginResponse.ok) {
  const loginData = await loginResponse.json();
  // In signup mode, if login succeeds, user already exists
  if (isSignup) {
    setSocialError("Account already exists. Please sign in instead.");
    try {
      await signOut(auth);
    } catch (signOutErr) {
      console.error("Error signing out:", signOutErr);
    }
    return;
  }
  // In signin mode, proceed with login
  accessToken = loginData.access_token;
  refreshToken = loginData.refresh_token || null;
        } else {
          // Login failed
          const loginError = await loginResponse.json().catch(() => ({}));
          console.error("Login failed:", loginError);

          // *** CRITICAL: In signin mode, NEVER register ***
          if (!isSignup) {
            if (loginResponse.status === 401 || loginResponse.status === 404) {
              setSocialError("User doesn't exist. Please sign up first.");
            } else {
              setSocialError(
                loginError.detail || "Login failed. Please try again."
              );
            }
            // Delete the Firebase user that was just created
            try {
              await deleteUser(user);
            } catch (deleteErr) {
              console.error("Error deleting Firebase user:", deleteErr);
            }
            return; // *** STOP HERE - DO NOT CONTINUE ***
          }

          // *** Only reach here if isSignup === true ***
          // Now try to register
          if (isSignup) {
            // Explicit check again
            try {
              const registerResponse = await fetchWithTimeout(
                `${BACKEND_URL}/auth/registerUser`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    email: user.email.toLowerCase(),
                    unique_id: user.uid,
                    password: user.uid,
                  }),
                },
                30000
              );

              if (registerResponse.ok) {
                const registerData = await registerResponse.json();
                accessToken = registerData.access_token;
                refreshToken = registerData.refresh_token || null;
              } else {
                const registerError = await registerResponse
                  .json()
                  .catch(() => ({}));
                console.error("Registration failed:", registerError);

                if (
                  registerResponse.status === 400 &&
                  registerError.detail === "Email already registered"
                ) {
                  setSocialError(
                    "Account already exists. Please sign in instead."
                  );
                } else {
                  setSocialError(
                    registerError.detail ||
                      "Authentication failed. Please contact support."
                  );
                }
                await deleteUser(user);
                return;
              }
            } catch (registerErr) {
              console.error("Backend API error:", registerErr);
              if (registerErr.name === "TimeoutError") {
                setSocialError("Request timeout - please try again");
              } else {
                setSocialError(
                  "Authentication successful but backend sync failed."
                );
              }
              await deleteUser(user);
              return;
            }
          }
        }
      } catch (loginErr) {
        console.error("Login attempt error:", loginErr);

        // In signin mode, show error and stop
        if (!isSignup) {
          if (loginErr.name === "TimeoutError") {
            setSocialError("Request timeout - please try again");
          } else {
            setSocialError("User doesn't exist. Please sign up first.");
          }
          try {
            await deleteUser(user);
          } catch (deleteErr) {
            console.error("Error deleting Firebase user:", deleteErr);
          }
          return; // *** STOP HERE ***
        }
        // If signup mode and login errored, we'd need to handle registration, but for safety, just error out
        setSocialError("Connection error. Please try again.");
        try {
          await deleteUser(user);
        } catch (deleteErr) {
          console.error("Error deleting Firebase user:", deleteErr);
        }
        return;
      }

      // Save token and complete login
      if (accessToken) {
        localStorage.setItem("access_token", accessToken);
        if (refreshToken) localStorage.setItem("refresh_token", refreshToken);
        setSocialError("");
        setError("");
        if (onSuccess) {
          const message = isSignup
            ? "User signed up successfully"
            : "User signed in successfully";
          onSuccess(message);
        }
        if (onSubmit) onSubmit();
      } else {
        setSocialError("Authentication failed. Please try again.");
        await deleteUser(user);
      }
    } catch (err) {
      console.error("Google Sign-In error:", err);
      setSocialError(
        err?.message || "Google Sign-In failed. Please try again."
      );
    }
  }
};
  // --------------------------
  // Email/Password Submit Handler
  // --------------------------
 /*
   const handleSubmit = async () => {
    if (isSubmitDisabled) return;

    if (!isValidEmail(inputValues.email)) {
      setError("Please enter a valid email address");
      return;
    }

    if (!isValidPassword(inputValues.password)) {
      setError(
        isSignup
          ? "Password must have: min 8 characters, 1 uppercase, 1 digit, 1 special character"
          : "Invalid password. Please try again."
      );
      return;
    }

    if (isSignup && confirmMismatch) {
      setError("Password and Confirm Password must match");
      return;
    }

    try {
      if (isSignup) {
        // Firebase Sign Up
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          inputValues.email,
          inputValues.password
        );
        const user = userCredential.user;

        // Backend register
        try {
          const response = await fetchWithTimeout(
            `${BACKEND_URL}/auth/registerUser`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                email: inputValues.email,
                unique_id: user.uid,
                password: inputValues.password,
              }),
            },
            30000
          );

          if (!response.ok || response.status !== 201) {
            const errorData = await response.json().catch(() => ({}));
            await deleteUser(user);
            setError(
              errorData.detail ||
                "There was an error registering your account."
            );
            return;
          }

          const data = await response.json();
          localStorage.setItem("access_token", data.access_token);
          if (data.refresh_token)
            localStorage.setItem("refresh_token", data.refresh_token);

          setError("");
          if (onSuccess) onSuccess("User signed up successfully");
        } catch (backendErr) {
          await deleteUser(user);
          setError("There was an error registering your account.");
          return;
        }
      } else {
        // Firebase Sign In
        const userCredential = await signInWithEmailAndPassword(
          auth,
          inputValues.email,
          inputValues.password
        );
        const firebaseUser = userCredential.user;

        try {
          const response = await fetchWithTimeout(
            `${BACKEND_URL}/auth/loginUser`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                email: inputValues.email,
                password: inputValues.password,
              }),
            },
            30000
          );

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));

            if (errorData.detail === "Incorrect email or password") {
              // Password changed in Firebase → sync to backend
              try {
                const syncResponse = await fetchWithTimeout(
                  `${BACKEND_URL}/auth/syncPassword`,
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      email: inputValues.email,
                      new_password: inputValues.password,
                      unique_id: firebaseUser.uid,
                    }),
                  },
                  30000
                );

                if (syncResponse.ok) {
                  const retryResponse = await fetchWithTimeout(
                    `${BACKEND_URL}/auth/loginUser`,
                    {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        email: inputValues.email,
                        password: inputValues.password,
                      }),
                    },
                    30000
                  );

                  if (retryResponse.ok) {
                    const retryData = await retryResponse.json();
                    localStorage.setItem(
                      "access_token",
                      retryData.access_token
                    );
                    if (retryData.refresh_token)
                      localStorage.setItem(
                        "refresh_token",
                        retryData.refresh_token
                      );

                    if (onSuccess) onSuccess("User signed in successfully");
                    setError("");
                  } else {
                    setError("Failed to sync password. Try again.");
                    return;
                  }
                } else {
                  setError("Failed to sync password.");
                  return;
                }
              } catch {
                setError("Failed to sync password.");
                return;
              }
            } else {
              setError(errorData.detail || "Backend login failed.");
              return;
            }
          } else {
            const data = await response.json();
            localStorage.setItem("access_token", data.access_token);
            if (data.refresh_token)
              localStorage.setItem("refresh_token", data.refresh_token);

            if (onSuccess) onSuccess("User signed in successfully");
            setError("");
          }
        } catch {
          setError("Firebase login successful but backend sync failed.");
          return;
        }
      }

      if (onSubmit) onSubmit();
    } catch (err) {
      switch (err.code) {
        case "auth/weak-password":
          setError("Password should be at least 6 characters.");
          break;
        case "auth/email-already-in-use":
          setError("This email is already registered. Please sign in.");
          break;
        case "auth/invalid-credential":
        case "auth/invalid-login-credentials":
          setError("Invalid credentials. Please check your email or password.");
          break;
        case "auth/user-not-found":
          setError("No account found with this email.");
          break;
        case "auth/wrong-password":
          setError("Incorrect password. Try again.");
          break;
        case "auth/invalid-email":
          setError("Please enter complete email address");
          break;
        default:
          setError("Something went wrong. Please try again later.");
      }
    }
  };
*/
  const handleSubmit = async () => {
  if (isSubmitDisabled) return;
  if (!isSignup && emailError) {
      setError(emailError);
      return;
    }
  if (!isValidEmail(inputValues.email)) {
    setError("Please enter a valid email address");
    return;
  }
 
  if (!isValidPassword(inputValues.password)) {
    setError(
      isSignup
        ? "Password must have: min 8 characters, 1 uppercase, 1 digit, 1 special character"
        : "Invalid password. Please try again."
    );
    return;
  }
 
  if (isSignup && confirmMismatch) {
    setError("Password and Confirm Password must match");
    return;
  }
 
  try {
    if (isSignup) {
      // 🔹 Firebase Sign Up
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        inputValues.email,
        inputValues.password
      );
      const user = userCredential.user;
 
      // 🔹 Backend register
      try {
        const response = await fetchWithTimeout(
          `${BACKEND_URL}/auth/registerUser`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: inputValues.email.toLowerCase(),
              unique_id: user.uid,
              password: inputValues.password,
              initial_credits: isFromContactForm ? 100 : undefined,
	    }),
          },
          30000
        );
 
        if (!response.ok || (response.status !== 200 && response.status !== 201)) {
          const errorData = await response.json().catch(() => ({}));
          await deleteUser(user);
          setError(
            errorData.detail ||
              "There was an error registering your account. Please try again."
          );
          return;
        }
 
        const data = await response.json().catch(() => ({}));
        if (data?.access_token) localStorage.setItem("access_token", data.access_token);
        if (data?.refresh_token)
          localStorage.setItem("refresh_token", data.refresh_token);
         if (isFromContactForm && contactSignupData?.businessEmail) {
            await submitLeadFormForContactUser(user.uid);
            triggerSignupCelebration();
          }
        setError("");
        if (onSuccess) onSuccess("User signed up successfully");
      } catch (backendErr) {
        await deleteUser(user);
        console.error("Backend registration error:", backendErr);
        setError("There was an error registering your account. Please try again.");
        return;
      }
    } else {
      // 🔹 Firebase Sign In
      const userCredential = await signInWithEmailAndPassword(
        auth,
        inputValues.email,
        inputValues.password
      );
      const firebaseUser = userCredential.user;
 
      try {
        const response = await fetchWithTimeout(
          `${BACKEND_URL}/auth/loginUser`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: inputValues.email.toLowerCase(),
              password: inputValues.password,
            }),
          },
          30000
        );
 
        // ✅ SUCCESS: Accept both 200 and 201 and parse safely
        if (response.status === 200 || response.status === 201) {
          let data = {};
          try {
            data = await response.json();
          } catch (jsonErr) {
            console.warn("Response had no JSON body, skipping parse");
          }
 
          if (data?.access_token)
            localStorage.setItem("access_token", data.access_token);
          if (data?.refresh_token)
            localStorage.setItem("refresh_token", data.refresh_token);
 
          setError("");
          if (onSuccess) onSuccess("User signed in successfully");
        } else {
          // ❌ Handle backend login failure
          const errorData = await response.json().catch(() => ({}));
 
          // Check if backend password is outdated — then sync it
          if (errorData.detail === "Incorrect email or password") {
            console.log("⚠ Backend password outdated, syncing with Firebase password...");
 
            try {
              const syncResponse = await fetchWithTimeout(
                `${BACKEND_URL}/auth/syncPassword`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    email: inputValues.email.toLowerCase(),
                    new_password: inputValues.password,
                    unique_id: firebaseUser.uid,
                  }),
                },
                30000
              );
 
              if (syncResponse.ok) {
                console.log("✅ Password synced, retrying backend login...");
 
                const retryResponse = await fetchWithTimeout(
                  `${BACKEND_URL}/auth/loginUser`,
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      email: inputValues.email.toLowerCase(),
                      password: inputValues.password,
                    }),
                  },
                  30000
                );
 
                if (retryResponse.status === 200 || retryResponse.status === 201) {
                  const retryData = await retryResponse.json().catch(() => ({}));
                  if (retryData?.access_token)
                    localStorage.setItem("access_token", retryData.access_token);
                  if (retryData?.refresh_token)
                    localStorage.setItem("refresh_token", retryData.refresh_token);
 
                  setError("");
                  if (onSuccess) onSuccess("User signed in successfully");
                } else {
                  setError("Failed to sync password. Please try again.");
                  return;
                }
              } else {
                setError("Failed to sync password with backend. Please contact support.");
                return;
              }
            } catch (syncErr) {
              console.error("Password sync error:", syncErr);
              setError("Failed to sync password. Please try again.");
              return;
            }
          } else {
            console.error("Backend login error:", errorData);
            setError(errorData.detail || "Backend login failed. Please try again.");
            return;
          }
        }
      } catch (backendErr) {
        console.error("Backend API error:", backendErr);
        setError("Firebase login successful but backend sync failed.");
        return;
      }
    }
 
    // ✅ Close dialog or proceed
    if (onSubmit) onSubmit();
  } catch (err) {
    console.error("Auth error:", err.code, err.message);
    switch (err.code) {
      case "auth/weak-password":
        setError("Password should be at least 6 characters.");
        break;
      case "auth/email-already-in-use":
        setError("This email is already registered. Please sign in.");
        break;
      case "auth/invalid-credential":
      case "auth/invalid-login-credentials":
        setError("Invalid credentials. Please check your email or password.");
        break;
      case "auth/user-not-found":
        setError("No account found with this email.");
        break;
      case "auth/wrong-password":
        setError("Incorrect password. Try again.");
        break;
      case "auth/invalid-email":
        setError("Please enter complete email address.");
        break;
      default:
        setError("Something went wrong. Please try again later.");
    }
  }
};
  // --------------------------
  // UI
  // --------------------------
  return (
    <Grid
      sx={{
        alignItems: "center",
        pt: { xs: 2, sm: 3 },
        pb: { xs: 2, sm: 2.5 },
        px: { xs: 1.5, sm: 2.5 },
        py: { xs: 1.5, sm: 2.5 },
        width: "100%",
        maxWidth: { xs: "100%", sm: 380, md: 420, lg: 440 },
        mx: "auto",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: "bold",
          color: theme.palette.primary.main,
          textAlign: "center",
          mb: 2,
        }}
      >
        {isSignup ? "Create Account" : "Sign In"}
      </Typography>

      {fields
        .filter((f) => f.show)
        .map((field) => (
          <Grid key={field.name}>
            <Typography variant="body2" sx={{ color: "#464545" }} gutterBottom>
              {field.label}
            </Typography>
	 {field.name === "email" && isSignup && isFromContactForm ? (
              <>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <TextField
                    type="email"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={inputValues[field.name]}
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{
                      mb: 1,
                      "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                    }}
                  />
                  {isEmailVerified ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 1,
                        color: theme.palette.success.main,
                        fontSize: "0.8rem",
                        fontWeight: 600,
                      }}
                    >
                      <CheckCircleIcon sx={{ fontSize: 30 }} />
                    </Box>
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleEmailVerifyClick}
                      disabled={isVerifyingEmail}
                      sx={{
                        backgroundColor: "#000",
                        color: "#fff",
                        minWidth: "80px",
                        height: "36px",
                        fontSize: "0.75rem",
                        padding: "4px 12px",
                        borderRadius: "8px",
                        flexShrink: 0,
                        mb: 1,
                        "&:hover": { backgroundColor: "#333" },
                        "&:disabled": {
                          backgroundColor: "#ccc",
                          color: "#666",
                        },
                      }}
                    >
                      {isVerifyingEmail ? (
                        <CircularProgress size={16} sx={{ color: "#fff" }} />
                      ) : (
                        "Verify"
                      )}
                    </Button>
                  )}
                </Box>
                {hasRequestedVerification && emailVerificationMessage && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: isEmailVerified
                        ? theme.palette.success.main
                        : "#1e88e5",
                      fontSize: "0.7rem",
                      mb: 1,
                    }}
                  >
                    {emailVerificationMessage}
                  </Typography>
                )}
              </>
            ) : (

            <TextField
              type={
                field.type === "password"
                  ? field.name === "password"
                    ? showPassword
                      ? "text"
                      : "password"
                    : showConfirmPassword
                    ? "text"
                    : "password"
                  : field.type
              }
              variant="outlined"
              fullWidth
              size="small"
              value={inputValues[field.name]}
              onChange={(e) => {
                setInputValues({ ...inputValues, [field.name]: e.target.value });
                   // Clear email validation state when user edits email
                if (field.name === "email") {
                  if (emailError) {
		      setEmailError("");
                  }
                  if (emailTouched) {
                    setEmailTouched(false);
                  }
                }
              }}
	      onBlur={
                  field.name === "email" && !(isSignup && isFromContactForm)
                    ? handleEmailBlur
                    : undefined
                }	    
	      error={Boolean(field.error)}
              helperText={field.helperText || ""}
              InputProps={
                field.type === "password"
                  ? {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              field.name === "password"
                                ? setShowPassword(!showPassword)
                                : setShowConfirmPassword(
                                    !showConfirmPassword
                                  )
                            }
                            edge="end"
                            size="small"
                          >
                            {(field.name === "password"
                              ? showPassword
                              : showConfirmPassword) ? (
                              <VisibilityOff fontSize="small" />
                            ) : (
                              <Visibility fontSize="small" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }
                  : {}
              }
              sx={{
                mb: 1,
                "& .MuiOutlinedInput-root": { borderRadius: "10px" },
              }}
            />
	    )}

            {field.name === "password" &&
              isSignup &&
              inputValues.password &&
              !isValidPassword(inputValues.password) && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "#d32f2f",
                    fontSize: "0.7rem",
                    display: "block",
                    mb: 1,
                  }}
                >
                  Password must have: min 8 characters, 1 uppercase, 1 digit, 1
                  special character
                </Typography>
              )}
          </Grid>
        ))}

      {mode === "signin" && (
        <Grid container justifyContent="flex-end">
          <Button
            size="small"
            onClick={onForgotPassword}
            sx={{
              color: "#464545",
              padding: 0,
              textTransform: "none",
              textDecoration: "underline",
            }}
          >
            Forgot password?
          </Button>
        </Grid>
      )}

      {error && (
        <Typography variant="caption" color="error" textAlign="center">
          {error}
        </Typography>
      )}

      <Grid sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <ReCAPTCHA
          sitekey={siteKey}
          onChange={(token) => setRecaptchaToken(token || "")}
          onExpired={() => setRecaptchaToken("")}
        />
      </Grid>

      <Button
        variant="contained"
        fullWidth
        size="small"
        disabled={isSubmitDisabled}
        sx={{
          backgroundColor: "#000",
          color: "#fff",
          padding: "8px",
          borderRadius: "10px",
          "&:hover": { backgroundColor: "#333" },
        }}
        onClick={handleSubmit}
      >
        {isSignup ? "Sign up" : "Sign in"}
      </Button>
      {!isFromContactForm && (
        <>
      <Divider sx={{ py: 1 }}>or</Divider>

      <Typography variant="body2" textAlign="center">
        Continue with
      </Typography>

      <Grid sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 1 }}>
        {socialProviders.map((provider) => (
          <Button
            key={provider.label}
            variant="outlined"
            aria-label={`Continue with ${provider.label}`}
            sx={{
              minWidth: 0,
              p: 1,
              borderRadius: "50px",
              borderColor: "#e0e0e0",
            }}
            onClick={() => handleSocialLogin(provider.key)}
          >
            <img
              src={provider.src}
              alt={provider.label}
              style={{ width: 20, height: 20 }}
            />
          </Button>
        ))}
      </Grid>

      {socialError && (
        <Typography
          variant="caption"
          color="error"
          textAlign="center"
          display="block"
          sx={{ mt: 1 }}
        >
          {socialError}
        </Typography>
      )}

       </>
      )}
      <Grid mt={2} textAlign="center"> 
	      <Typography variant="body2" sx={{ color: "#464545" }}>
          {isSignup ? "Already have an account?" : "New User?"} &nbsp;
          <Button
            variant="text"
            sx={{ textTransform: "none", p: 0, minWidth: "auto" }}
            onClick={() => {
              setMode(isSignup ? "signin" : "signup");
              setError("");
              setSocialError("");
	      setEmailError("");
              setEmailTouched(false);
              setEmailCheckLoading(false);
            }}
          >
            {isSignup ? "Sign in" : "Register"}
          </Button>
        </Typography>
      </Grid>
    </Grid>
  );
};

export default AuthForm;

